<template>
  <div class="col-sm-12">
    <div class="card">
      <div class="card-body">
        <!-- Download Section -->
        <div class="dropdown-download-container">

          <div class="dropdown-item">
            <label for="targetTypeSelect">Select Type</label>
            <select
              id="targetTypeSelect"
              v-model="targetType"
              class="search-input"
              :disabled="fileUploaded"
            >
              <option value="vehicle">Vehicle Target</option>
              <option value="se">SE Target</option>
            </select>
          </div>

          <div class="dropdown-item">
            <label for="monthYearPicker">Select Year &amp; Month</label>
            <input
              type="month"
              v-model="monthYear"
              class="search-input"
              @change="handleMonthYearChange"
              id="monthYearPicker"
              :disabled="fileUploaded"
            />
          </div>

          <div class="dropdown-item">
            <label for="distributorSelect">
              Select Distributor
              <span v-if="loading1" class="spinner-border spinner-border-sm ms-1" role="status" aria-hidden="true" style="margin-left: 5px;"></span>
            </label>
            <select
              id="distributorSelect"
              v-model="selectedDistributorId"
              class="search-input"
              @change="handleDistributorChange"
              :disabled="loading1 || isDistributorLocked || fileUploaded"
            >
              <option value="">-- Select Distributor --</option>
              <option
                v-for="dist in distributors"
                :key="dist.profile_id"
                :value="dist.profile_id"
              >
                {{ dist.name }}
              </option>
            </select>
          </div>

          <button
            class="download-button"
            @click="downloadExcel"
            :disabled="downloading || !monthYear || !selectedDistributorId || fileUploaded"
          >
            <span v-if="downloading" class="spinner-border spinner-border-sm"></span>
            <span v-else>
              <i class="fa-solid fa-arrow-down"></i>
              Download Template
            </span>
          </button>

        </div>

        <!-- Upload Section -->
        <div 
          v-if="!fileUploaded"
          class="upload-drop-zone"
          @dragover.prevent="dragOver = true"
          @dragleave.prevent="dragOver = false"
          @drop.prevent="handleDrop"
          @click="triggerFileInput"
          :class="{ 'drag-over': dragOver }"
        >
          <input 
            type="file" 
            ref="fileInput" 
            @change="handleFileUpload" 
            accept=".xls,.xlsx" 
            style="display: none;" 
          />
          <div class="upload-content">
            <i class="fa-solid fa-cloud-arrow-up upload-icon"></i>
            <p class="upload-title">Drag & drop your Excel file here</p>
            <p class="upload-subtitle">or click to browse from your computer</p>
          </div>
        </div>

        <!-- Preview Table -->
        <div v-if="tableData.length" class="table-preview-wrapper">
          <div class="table-preview-header">
            <span class="table-preview-title">
              <i class="fa-solid fa-table"></i>
              Preview — {{ tableData.length }} row(s)
            </span>
            <span class="table-preview-hint">
              <i class="fa-solid fa-arrows-left-right"></i>
              Scroll horizontally to see all columns
            </span>
          </div>
          <div class="table-scroll-container">
            <table class="preview-table">
              <thead>
                <tr>
                  <th v-for="(header, index) in headers" :key="index">{{ header }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(row, rowIndex) in tableData" :key="rowIndex">
                  <td v-for="(cell, cellIndex) in row" :key="cellIndex">{{ cell }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Action Buttons -->
        <div v-if="fileUploaded" class="bottom-action-container mt-4 d-flex justify-content-end" style="gap: 12px;">
          <button
            class="button-cancel"
            @click="resetFileState"
            :disabled="submitting"
          >
            Cancel
          </button>
          <button
            class="button-secondary"
            @click="handleSubmit"
            :disabled="submitting || !isFileValid"
          >
            <span v-if="submitting" class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
            <span v-else>Submit</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import * as XLSX from "xlsx";
import { fetchDistributors, downloadVehicleTargetMatrix, downloadSeTargetMatrix, saveVehicleTarget, saveSeTarget } from "@/services/dataService";
import { useToast } from "vue-toastification";

export default {
  data() {
    return {
      targetType: "vehicle",
      tableData: [],
      headers: [],
      variantIdRow: [],
      lorryIdRow: [],
      isFileValid: false,
      isDistributorLocked: false,
      distTargetMap: [],

      token:     localStorage.getItem("token_set"),
      companyId: localStorage.getItem("company_id"),
      profileId: localStorage.getItem("profile_id"),

      downloading:  false,
      fileUploaded: false,
      submitting:   false,
      dragOver:     false,

      distributors:          [],
      selectedDistributorId: "",
      loading1:              false,

      currentMonthYear: (() => {
        const now = new Date();
        const y = now.getFullYear();
        const m = String(now.getMonth() + 1).padStart(2, '0');
        return `${y}-${m}`;
      })(),
      monthYear: (() => {
        const now = new Date();
        const y = now.getFullYear();
        const m = String(now.getMonth() + 1).padStart(2, '0');
        return `${y}-${m}`;
      })(),
    };
  },

  created() {
    this.fetchDistributorsList();
  },

  setup() {
    const toast = useToast();
    return { toast };
  },

  methods: {
    // ─── Fetch distributors ───────────────────────────────────────────────────
    async fetchDistributorsList() {
      try {
        this.loading1 = true;
        const response = await fetchDistributors(this.profileId, this.token);
        this.distributors = response.data.data;

        if (this.distributors.length === 1) {
          const only = this.distributors[0];
          const onlyId = String(only.profile_id ?? only.id);

          if (onlyId === String(this.profileId)) {
            this.selectedDistributorId = onlyId;
            this.isDistributorLocked = true;
          } else {
            this.selectedDistributorId = "";
            this.isDistributorLocked = false;
          }
        } else {
          this.isDistributorLocked = false;
        }
      } catch (e) {
        console.error("fetchDistributors error", e);
      } finally {
        this.loading1 = false;
      }
    },

    // ─── Drag & Drop Handlers ────────────────────────────────────────────────
    triggerFileInput() {
      if (this.$refs.fileInput) {
        this.$refs.fileInput.click();
      }
    },
    
    handleDrop(event) {
      this.dragOver = false;
      const files = event.dataTransfer.files;
      if (files && files.length > 0) {
        this.handleFileUpload({ target: { files: files } });
      }
    },

    // ─── Change handlers ──────────────────────────────────────────────────────
    handleMonthYearChange() {
      this.resetFileState();
    },

    handleDistributorChange() {
      this.resetFileState();
    },

    resetFileState() {
      this.tableData    = [];
      this.headers      = [];
      this.variantIdRow = [];
      this.lorryIdRow   = [];
      this.distTargetMap = {};
      this.isFileValid  = false;
      this.fileUploaded = false;
      if (this.$refs.fileInput) this.$refs.fileInput.value = "";
    },

    // ─── Helper: column letter from 0-based index ─────────────────────────────
    colLetter(idx) {
      let letter = "", n = idx;
      while (n >= 0) {
        letter = String.fromCharCode((n % 26) + 65) + letter;
        n = Math.floor(n / 26) - 1;
      }
      return letter;
    },

    // ─── Download Excel template ──────────────────────────────────────────────
    async downloadExcel() {
      if (this.targetType === 'se') {
        await this.downloadSeTargetMatrix();
      } else {
        await this.downloadVehicleTargetMatrix();
      }
    },

    async downloadVehicleTargetMatrix() {
      if (!this.monthYear) {
        this.toast.error("Please select a year and month first.");
        return;
      }
      if (!this.selectedDistributorId) {
        this.toast.error("Please select a distributor first.");
        return;
      }

      try {
        this.downloading = true;
        const [year, month] = this.monthYear.split("-").map(Number);

        const matrixRes = await downloadVehicleTargetMatrix(
          { year, month, distributor_id: this.selectedDistributorId },
          this.token
        );
        this.distTargetMap = {};
        matrixRes.data.data.items.forEach(item => {
          this.distTargetMap[item.variant_id] = Number(item.distributor_target);
        });

        if (matrixRes.data.status !== "success") {
          this.toast.error("Failed to generate template.");
          return;
        }

        const { vehicles, items } = matrixRes.data.data;

        if (!vehicles || !vehicles.length) {
          this.toast.error("No vehicles found for this distributor.");
          return;
        }
        if (!items || !items.length) {
          this.toast.error("No distributor targets found for this month and distributor.");
          return;
        }

        const daysInMonth = new Date(year, month, 0).getDate();
        const dayNumbers  = Array.from({ length: daysInMonth }, (_, i) => i + 1);

        const typeGroups = [];
        const typeMap    = {};
        items.forEach(item => {
          if (!typeMap[item.type_id]) {
            typeMap[item.type_id] = { type_id: item.type_id, type_name: item.name, items: [] };
            typeGroups.push(typeMap[item.type_id]);
          }
          typeMap[item.type_id].items.push(item);
        });

        const DIST_TARGET_ROW = 1;
        const TYPE_ROW        = 2;
        const VARIANT_ROW     = 3;
        const ID_ROW          = 4;
        const META_ROW        = 5;
        const DATA_START_ROW  = 6;

        const FIXED_COL_COUNT = 3;
        const ws     = {};
        const merges = [];

        const setCell = (col, row, value, style = {}) => {
          const ref = `${this.colLetter(col)}${row}`;
          ws[ref] = { v: value, t: typeof value === "number" ? "n" : "s", s: style };
        };

        const sDistTarget  = { font: { bold: true, color: { rgb: "FFFFFF" } }, fill: { fgColor: { rgb: "C00000" } }, alignment: { horizontal: "center", vertical: "center" } };
        const sTypeHeader  = { font: { bold: true, color: { rgb: "FFFFFF" } }, fill: { fgColor: { rgb: "2F5496" } }, alignment: { horizontal: "center", vertical: "center" } };
        const sItemHeader  = { font: { bold: true }, fill: { fgColor: { rgb: "BDD7EE" } }, alignment: { horizontal: "center", vertical: "center", wrapText: true } };
        const sIdHeader    = { font: { italic: true, color: { rgb: "888888" }, sz: 9 }, fill: { fgColor: { rgb: "F2F2F2" } }, alignment: { horizontal: "center" } };
        const sFixedHeader = { font: { bold: true, color: { rgb: "FFFFFF" } }, fill: { fgColor: { rgb: "1F3864" } }, alignment: { horizontal: "center", vertical: "center" } };
        const sLorryCell   = { font: { bold: true }, fill: { fgColor: { rgb: "DEEAF1" } }, alignment: { horizontal: "center", vertical: "center" } };
        const sDayCell     = { alignment: { horizontal: "center" } };

        ["Vehicle No", "Vehicle Id", "Day"].forEach((header, i) => {
          [DIST_TARGET_ROW, TYPE_ROW, VARIANT_ROW, ID_ROW].forEach(r => setCell(i, r, "", sFixedHeader));
          setCell(i, DIST_TARGET_ROW, header, sFixedHeader);
          merges.push({ s: { r: DIST_TARGET_ROW - 1, c: i }, e: { r: ID_ROW - 1, c: i } });
        });

        setCell(0, META_ROW, Number(this.selectedDistributorId), sIdHeader);
        setCell(1, META_ROW, year, sIdHeader);
        setCell(2, META_ROW, month, sIdHeader);

        let colIndex = FIXED_COL_COUNT;
        typeGroups.forEach(group => {
          const startCol = colIndex;
          group.items.forEach(item => {
            const distTarget = this.distTargetMap[item.variant_id] ?? "";
            setCell(colIndex, DIST_TARGET_ROW, distTarget !== "" ? Number(distTarget) : "", sDistTarget);
            setCell(colIndex, VARIANT_ROW, item.variant_name, sItemHeader);
            setCell(colIndex, ID_ROW, item.variant_id, sIdHeader);
            colIndex++;
          });
          const endCol = colIndex - 1;
          setCell(startCol, TYPE_ROW, group.type_name, sTypeHeader);
          if (startCol < endCol) {
            for (let c = startCol + 1; c <= endCol; c++) setCell(c, TYPE_ROW, "", sTypeHeader);
            merges.push({ s: { r: TYPE_ROW - 1, c: startCol }, e: { r: TYPE_ROW - 1, c: endCol } });
          }
        });

        const TOTAL_COLS = colIndex;

        let currentRow = DATA_START_ROW;
        vehicles.forEach(vehicle => {
          const vehicleStartRow = currentRow;

          dayNumbers.forEach((day, dayIdx) => {
            setCell(0, currentRow, dayIdx === 0 ? vehicle.vehicle_no : "", dayIdx === 0 ? sLorryCell : {});
            setCell(1, currentRow, dayIdx === 0 ? (vehicle.profile_id || "") : "", dayIdx === 0 ? sLorryCell : {});
            setCell(2, currentRow, day, sDayCell);
            for (let c = FIXED_COL_COUNT; c < TOTAL_COLS; c++) {
              setCell(c, currentRow, "");
            }
            currentRow++;
          });

          if (daysInMonth > 1) {
            merges.push({
              s: { r: vehicleStartRow - 1, c: 0 },
              e: { r: currentRow - 2,    c: 0 },
            });
            merges.push({
              s: { r: vehicleStartRow - 1, c: 1 },
              e: { r: currentRow - 2,    c: 1 },
            });
          }
        });

        const totalRows = currentRow - 1;
        ws["!ref"]    = `A1:${this.colLetter(TOTAL_COLS - 1)}${totalRows}`;
        ws["!merges"] = merges;

        const colWidths = [{ wch: 18 }, { wch: 15 }, { wch: 8 }];
        items.forEach(item => colWidths.push({ wch: Math.max(item.variant_name.length + 2, 14) }));
        ws["!cols"] = colWidths;

        const rowHeights = [
          { hpt: 20 },
          { hpt: 22 },
          { hpt: 30 },
          { hpt: 0, hidden: true },
          { hpt: 0, hidden: true },
        ];
        ws["!rows"] = rowHeights;

        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, ws, "Vehicle Target");
        XLSX.writeFile(workbook, `vehicle_target_${year}_${month}_dist${this.selectedDistributorId}.xlsx`);

      } catch (error) {
        console.error(error);
        this.toast.error("Error downloading file.");
      } finally {
        this.downloading = false;
      }
    },

    async downloadSeTargetMatrix() {
      if (!this.monthYear) {
        this.toast.error("Please select a year and month first.");
        return;
      }
      if (!this.selectedDistributorId) {
        this.toast.error("Please select a distributor first.");
        return;
      }

      try {
        this.downloading = true;
        const [year, month] = this.monthYear.split("-").map(Number);

        const matrixRes = await downloadSeTargetMatrix(
          { year, month, distributor_id: this.selectedDistributorId },
          this.token
        );
        this.distTargetMap = {};
        matrixRes.data.data.items.forEach(item => {
          this.distTargetMap[item.variant_id] = Number(item.distributor_target);
        });

        if (matrixRes.data.status !== "success") {
          this.toast.error("Failed to generate template.");
          return;
        }

        const { salesExecutives, items } = matrixRes.data.data;

        if (!salesExecutives || !salesExecutives.length) {
          this.toast.error("No Sales Executives found for this distributor.");
          return;
        }
        if (!items || !items.length) {
          this.toast.error("No distributor targets found for this month and distributor.");
          return;
        }

        const daysInMonth = new Date(year, month, 0).getDate();
        const dayNumbers  = Array.from({ length: daysInMonth }, (_, i) => i + 1);

        const typeGroups = [];
        const typeMap    = {};
        items.forEach(item => {
          if (!typeMap[item.type_id]) {
            typeMap[item.type_id] = { type_id: item.type_id, type_name: item.name, items: [] };
            typeGroups.push(typeMap[item.type_id]);
          }
          typeMap[item.type_id].items.push(item);
        });

        const DIST_TARGET_ROW = 1;
        const TYPE_ROW        = 2;
        const VARIANT_ROW     = 3;
        const ID_ROW          = 4;
        const META_ROW        = 5;
        const DATA_START_ROW  = 6;

        const FIXED_COL_COUNT = 3;
        const ws     = {};
        const merges = [];

        const setCell = (col, row, value, style = {}) => {
          const ref = `${this.colLetter(col)}${row}`;
          ws[ref] = { v: value, t: typeof value === "number" ? "n" : "s", s: style };
        };

        const sDistTarget  = { font: { bold: true, color: { rgb: "FFFFFF" } }, fill: { fgColor: { rgb: "C00000" } }, alignment: { horizontal: "center", vertical: "center" } };
        const sTypeHeader  = { font: { bold: true, color: { rgb: "FFFFFF" } }, fill: { fgColor: { rgb: "2F5496" } }, alignment: { horizontal: "center", vertical: "center" } };
        const sItemHeader  = { font: { bold: true }, fill: { fgColor: { rgb: "BDD7EE" } }, alignment: { horizontal: "center", vertical: "center", wrapText: true } };
        const sIdHeader    = { font: { italic: true, color: { rgb: "888888" }, sz: 9 }, fill: { fgColor: { rgb: "F2F2F2" } }, alignment: { horizontal: "center" } };
        const sFixedHeader = { font: { bold: true, color: { rgb: "FFFFFF" } }, fill: { fgColor: { rgb: "1F3864" } }, alignment: { horizontal: "center", vertical: "center" } };
        const sSeCell      = { font: { bold: true }, fill: { fgColor: { rgb: "DEEAF1" } }, alignment: { horizontal: "center", vertical: "center" } };
        const sDayCell     = { alignment: { horizontal: "center" } };

        ["SE Name", "SE Id", "Day"].forEach((header, i) => {
          [DIST_TARGET_ROW, TYPE_ROW, VARIANT_ROW, ID_ROW].forEach(r => setCell(i, r, "", sFixedHeader));
          setCell(i, DIST_TARGET_ROW, header, sFixedHeader);
          merges.push({ s: { r: DIST_TARGET_ROW - 1, c: i }, e: { r: ID_ROW - 1, c: i } });
        });

        setCell(0, META_ROW, Number(this.selectedDistributorId), sIdHeader);
        setCell(1, META_ROW, year, sIdHeader);
        setCell(2, META_ROW, month, sIdHeader);

        let colIndex = FIXED_COL_COUNT;
        typeGroups.forEach(group => {
          const startCol = colIndex;
          group.items.forEach(item => {
            const distTarget = this.distTargetMap[item.variant_id] ?? "";
            setCell(colIndex, DIST_TARGET_ROW, distTarget !== "" ? Number(distTarget) : "", sDistTarget);
            setCell(colIndex, VARIANT_ROW, item.variant_name, sItemHeader);
            setCell(colIndex, ID_ROW, item.variant_id, sIdHeader);
            colIndex++;
          });
          const endCol = colIndex - 1;
          setCell(startCol, TYPE_ROW, group.type_name, sTypeHeader);
          if (startCol < endCol) {
            for (let c = startCol + 1; c <= endCol; c++) setCell(c, TYPE_ROW, "", sTypeHeader);
            merges.push({ s: { r: TYPE_ROW - 1, c: startCol }, e: { r: TYPE_ROW - 1, c: endCol } });
          }
        });

        const TOTAL_COLS = colIndex;

        let currentRow = DATA_START_ROW;
        salesExecutives.forEach(executive => {
          const seStartRow = currentRow;

          dayNumbers.forEach((day, dayIdx) => {
            setCell(0, currentRow, dayIdx === 0 ? executive.full_name : "", dayIdx === 0 ? sSeCell : {});
            setCell(1, currentRow, dayIdx === 0 ? (executive.profile_id || "") : "", dayIdx === 0 ? sSeCell : {});
            setCell(2, currentRow, day, sDayCell);
            for (let c = FIXED_COL_COUNT; c < TOTAL_COLS; c++) {
              setCell(c, currentRow, "");
            }
            currentRow++;
          });

          if (daysInMonth > 1) {
            merges.push({
              s: { r: seStartRow - 1, c: 0 },
              e: { r: currentRow - 2,    c: 0 },
            });
            merges.push({
              s: { r: seStartRow - 1, c: 1 },
              e: { r: currentRow - 2,    c: 1 },
            });
          }
        });

        const totalRows = currentRow - 1;
        ws["!ref"]    = `A1:${this.colLetter(TOTAL_COLS - 1)}${totalRows}`;
        ws["!merges"] = merges;

        const colWidths = [{ wch: 25 }, { wch: 15 }, { wch: 8 }];
        items.forEach(item => colWidths.push({ wch: Math.max(item.variant_name.length + 2, 14) }));
        ws["!cols"] = colWidths;

        const rowHeights = [
          { hpt: 20 },
          { hpt: 22 },
          { hpt: 30 },
          { hpt: 0, hidden: true },
          { hpt: 0, hidden: true },
        ];
        ws["!rows"] = rowHeights;

        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, ws, "SE Target");
        XLSX.writeFile(workbook, `se_target_${year}_${month}_dist${this.selectedDistributorId}.xlsx`);

      } catch (error) {
        console.error(error);
        this.toast.error("Error downloading file.");
      } finally {
        this.downloading = false;
      }
    },

    // ─── Handle file upload & validation ─────────────────────────────────────
    handleFileUpload(event) {
      const file = event.target.files[0];
      if (!file) return;

      this.tableData    = [];
      this.headers      = [];
      this.variantIdRow = [];
      this.lorryIdRow   = [];
      this.isFileValid  = false;
      this.fileUploaded = false;

      if (!this.monthYear) {
        this.toast.error("Please select a year and month before uploading.");
        if (this.$refs.fileInput) this.$refs.fileInput.value = "";
        return;
      }
      if (!this.selectedDistributorId) {
        this.toast.error("Please select a distributor before uploading.");
        if (this.$refs.fileInput) this.$refs.fileInput.value = "";
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        const data     = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: "array" });
        const sheet    = workbook.Sheets[workbook.SheetNames[0]];
        const jsonData = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: "" });

        const distTargetRow  = jsonData[0] || [];
        const variantNameRow = jsonData[2] || [];
        const variantIdRow   = jsonData[3] || [];
        const metaRow        = jsonData[4] || [];
        const dataRows       = jsonData.slice(5);

        const fileTypeHeader = distTargetRow[0];
        if (this.targetType === 'se' && fileTypeHeader !== 'SE Name') {
          this.toast.error("Invalid file format. Please upload an SE Target template.");
          if (this.$refs.fileInput) this.$refs.fileInput.value = "";
          return;
        }
        if (this.targetType === 'vehicle' && fileTypeHeader !== 'Vehicle No') {
          this.toast.error("Invalid file format. Please upload a Vehicle Target template.");
          if (this.$refs.fileInput) this.$refs.fileInput.value = "";
          return;
        }

        const FIXED_COL_COUNT = 3;
        const [selectedYear, selectedMonth] = this.monthYear.split("-").map(Number);

        const fileDistributorId = parseInt(metaRow[0]);
        const fileYear          = parseInt(metaRow[1]);
        const fileMonth         = parseInt(metaRow[2]);

        if (fileDistributorId !== Number(this.selectedDistributorId) ||
            fileYear  !== selectedYear ||
            fileMonth !== selectedMonth) {
          this.toast.error(
            `Wrong file: this file is for a different distributor or period. ` +
            `Please download the correct template.`
          );
          if (this.$refs.fileInput) this.$refs.fileInput.value = "";
          return;
        }

        // ── Build column maps ──
        const columnNameMap = {};
        variantNameRow.forEach((name, idx) => {
          if (idx >= FIXED_COL_COUNT && name !== "") columnNameMap[idx] = name;
        });

        const variantIdMap = {};
        variantIdRow.forEach((cell, idx) => {
          if (idx >= FIXED_COL_COUNT && cell !== "" && cell !== null) {
            const parsed = parseInt(String(cell));
            if (!isNaN(parsed)) variantIdMap[idx] = parsed;
          }
        });

        const distTargetMap = {};
        variantIdRow.forEach((cell, idx) => {
          if (idx >= FIXED_COL_COUNT && cell !== "" && cell !== null) {
            const variantId = parseInt(String(cell));
            const target    = distTargetRow[idx];
            if (!isNaN(variantId) && target !== "" && target !== null && target !== undefined) {
              distTargetMap[variantId] = Number(target);
            }
          }
        });

        const errors = [];
        let hasAtLeastOneFilledValue = false;

        // ── Check 1: Symbol / format validation ──
        dataRows.forEach((row, rowIdx) => {
          const displayRow = rowIdx + 1;
          Object.keys(columnNameMap).forEach(colIdxStr => {
            const colIdx    = parseInt(colIdxStr);
            const cellValue = row[colIdx];
            const colName   = columnNameMap[colIdx];
            if (cellValue === "" || cellValue === null || cellValue === undefined) return;
            const strValue = String(cellValue).trim();

            if (strValue !== "") {
              hasAtLeastOneFilledValue = true;
            }

            if (/[a-zA-Z]/.test(strValue)) {
              errors.push(`Row ${displayRow}, "${colName}": Letters are not allowed. Found "${strValue}".`);
            } else if (/[^0-9.]/.test(strValue)) {
              errors.push(`Row ${displayRow}, "${colName}": Special characters are not allowed. Found "${strValue}".`);
            } else if (strValue.includes(".")) {
              errors.push(`Row ${displayRow}, "${colName}": Decimals are not allowed. Found "${strValue}". Enter a whole number.`);
            }
          });
        });

        if (!hasAtLeastOneFilledValue) {
          errors.push("The template is empty. Please fill at least one target value before uploading.");
        }

        // ── Check 2: Grand total per item must not exceed distributor target ──
        // (Removed: target uploading to vehicle or SE target can be more, less, or equal to the distributor target)

        // ── Build preview ──
        this.headers = this.targetType === 'se' 
          ? ["SE Name", "SE Id", "Day", ...Object.values(columnNameMap)] 
          : ["Vehicle No", "Vehicle Id", "Day", ...Object.values(columnNameMap)];
        this.variantIdRow = variantIdRow;
        this.tableData    = dataRows;
        this.fileUploaded = true;
        if (this.$refs.fileInput) this.$refs.fileInput.value = "";

        if (errors.length > 0) {
          const errorText = errors.map((e, i) => `${i + 1}. ${e}`).join("\n\n");
          this.toast.error(`${errors.length} validation error(s) found:\n\n${errorText}`);
          this.isFileValid = false;
        } else {
          this.toast.success("File validated successfully. Ready to submit.");
          this.isFileValid = true;
        }
      };

      reader.readAsArrayBuffer(file);
    },

    // ─── Submit ───────────────────────────────────────────────────────────────
    async handleSubmit() {
      if (this.targetType === 'se') {
        await this.handleSeTargetSubmit();
      } else {
        await this.handleVehicleTargetSubmit();
      }
    },

    async handleVehicleTargetSubmit() {
      try {
        this.submitting = true;

        if (!this.fileUploaded || !this.isFileValid) {
          this.toast.error("Please upload a valid file before submitting.");
          return;
        }

        const [year, month] = this.monthYear.split("-").map(Number);

        if (!year || !month) {
          this.toast.error("Please select a valid year and month.");
          return;
        }

        const FIXED_COL_COUNT = 3;

        // Build variantIdMap from stored variantIdRow
        const variantIdMap = {};
        this.variantIdRow.forEach((cell, colIdx) => {
          if (colIdx >= FIXED_COL_COUNT && cell !== "" && cell !== null && cell !== undefined) {
            const parsed = parseInt(String(cell));
            if (!isNaN(parsed)) variantIdMap[colIdx] = parsed;
          }
        });

        if (Object.keys(variantIdMap).length === 0) {
          this.toast.error("Invalid file format. Variant ID reference row not found.");
          return;
        }

        // Build payload
        const excelPayloadMap = {};
        let currentVehicleId = null;

        this.tableData.forEach(row => {
          const rawVehicleId = row[1];
          if (rawVehicleId !== "" && rawVehicleId !== null && rawVehicleId !== undefined) {
            currentVehicleId = Number(rawVehicleId);
          }
          const day = parseInt(row[2]);
          if (!currentVehicleId || isNaN(day)) return;

          if (!excelPayloadMap[currentVehicleId]) {
            excelPayloadMap[currentVehicleId] = {
              vehicle_id: currentVehicleId,
              target: []
            };
          }

          Object.entries(variantIdMap).forEach(([colIdxStr, variantId]) => {
            const colIdx      = parseInt(colIdxStr);
            const targetValue = row[colIdx];
            const numericTarget = (targetValue === null || targetValue === undefined || targetValue === "") 
              ? 0 
              : Number(targetValue);

            excelPayloadMap[currentVehicleId].target.push({
              day,
              variant_id: variantId,
              target: numericTarget,
            });
          });
        });

        const excelPayload = Object.values(excelPayloadMap);

        if (excelPayload.length === 0) {
          this.toast.error("No target data found in the uploaded file.");
          return;
        }

        const finalPayload = {
          target_created_by: this.profileId,
          distributor_profile_id: Number(this.selectedDistributorId),
          year,
          month,
          Excel: excelPayload,
        };

        const response = await saveVehicleTarget(finalPayload, this.token);

        if (response.status === 200) {
          this.toast.success("Vehicle targets saved successfully!");
          this.resetFileState();
        } else if (response.status === 422) {
          console.error("Validation errors:", response.data.errors);
          this.toast.error("Validation failed. Please check the file and try again.");
        } else {
          console.error("Server error:", response.data.error);
          this.toast.error("Server error. All changes were rolled back.");
        }

      } catch (e) {
        console.error("Error processing Vehicle target upload:", e);
        this.toast.error("Something went wrong. Please try again.");
      } finally {
        this.submitting = false;
      }
    },

    async handleSeTargetSubmit() {
      try {
        this.submitting = true;

        if (!this.fileUploaded || !this.isFileValid) {
          this.toast.error("Please upload a valid file before submitting.");
          return;
        }

        const [year, month] = this.monthYear.split("-").map(Number);

        if (!year || !month) {
          this.toast.error("Please select a valid year and month.");
          return;
        }

        const FIXED_COL_COUNT = 3;

        // Build variantIdMap from stored variantIdRow
        const variantIdMap = {};
        this.variantIdRow.forEach((cell, colIdx) => {
          if (colIdx >= FIXED_COL_COUNT && cell !== "" && cell !== null && cell !== undefined) {
            const parsed = parseInt(String(cell));
            if (!isNaN(parsed)) variantIdMap[colIdx] = parsed;
          }
        });

        if (Object.keys(variantIdMap).length === 0) {
          this.toast.error("Invalid file format. Variant ID reference row not found.");
          return;
        }

        // Build payload
        const excelPayloadMap = {};
        let currentSeId = null;

        this.tableData.forEach(row => {
          const rawSeId = row[1];
          if (rawSeId !== "" && rawSeId !== null && rawSeId !== undefined) {
            currentSeId = Number(rawSeId);
          }
          const day = parseInt(row[2]);
          if (!currentSeId || isNaN(day)) return;

          if (!excelPayloadMap[currentSeId]) {
            excelPayloadMap[currentSeId] = {
              se_profile_id: currentSeId,
              target: []
            };
          }

          Object.entries(variantIdMap).forEach(([colIdxStr, variantId]) => {
            const colIdx      = parseInt(colIdxStr);
            const targetValue = row[colIdx];
            const numericTarget = (targetValue === null || targetValue === undefined || targetValue === "") 
              ? 0 
              : Number(targetValue);

            excelPayloadMap[currentSeId].target.push({
              day,
              variant_id: variantId,
              target: numericTarget,
            });
          });
        });

        const excelPayload = Object.values(excelPayloadMap);

        if (excelPayload.length === 0) {
          this.toast.error("No target data found in the uploaded file.");
          return;
        }

        const finalPayload = {
          target_created_by:      this.profileId,
          distributor_profile_id: Number(this.selectedDistributorId),
          year,
          month,
          Excel: excelPayload,
        };

        const response = await saveSeTarget(finalPayload, this.token);

        if (response.status === 200) {
          this.toast.success("SE targets saved successfully!");
          this.resetFileState();
        } else if (response.status === 422) {
          console.error("Validation errors:", response.data.errors);
          this.toast.error("Validation failed. Please check the file and try again.");
        } else {
          console.error("Server error:", response.data.error);
          this.toast.error("Server error. All changes were rolled back.");
        }

      } catch (e) {
        console.error("Error processing SE target upload:", e);
        this.toast.error("Something went wrong. Please try again.");
      } finally {
        this.submitting = false;
      }
    },
  },
};
</script>
 
<style scoped>
.card {
  padding: 24px;
}

/* ── Download bar ── */
.dropdown-download-container {
  display: flex;
  align-items: flex-end;
  gap: 10px;
  flex-wrap: wrap;
  margin-bottom: 16px;
}

.dropdown-item {
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
  min-width: 180px;
  max-width: 450px;
}

label {
  font-weight: bold;
  font-size: 14px;
  margin-bottom: 0;
}

.search-input {
  width: 100%;
  height: 42px;
  box-sizing: border-box;
  padding: 10px;
  border-radius: 5px;
  border: 1px solid #ccc;
  font-size: 14px;
  transition: border-color 0.3s, box-shadow 0.3s;
}

.search-input:focus {
  border-color: #007bff;
  box-shadow: 0 0 5px rgba(38, 143, 255, 0.5);
  outline: none;
}

.download-button {
  margin-left: auto;
  background-color: #1D6F42;
  color: white;
  border: none;
  border-radius: 5px;
  height: 42px;
  padding: 0 16px;
  white-space: nowrap;
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  font-size: 14px;
}

.download-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* ── Upload Drop Zone ── */
.upload-drop-zone {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  padding: 40px 20px;
  background-color: #f8f9fa;
  border: 2px dashed #ced4da;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-bottom: 16px;
}

.upload-drop-zone:hover, .upload-drop-zone.drag-over {
  background-color: #e9ecef;
  border-color: #6c757d;
}

.upload-content {
  text-align: center;
}

.upload-icon {
  font-size: 48px;
  color: #adb5bd;
  margin-bottom: 16px;
}

.upload-title {
  font-weight: 600;
  font-size: 16px;
  color: #495057;
  margin-bottom: 8px;
}

.upload-subtitle {
  font-size: 14px;
  color: #6c757d;
  margin: 0;
}

/* ── Preview table wrapper ── */
.table-preview-wrapper {
  margin-top: 20px;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  overflow: hidden;
}

.table-preview-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 16px;
  background-color: #f1f3f5;
  border-bottom: 1px solid #dee2e6;
}

.table-preview-title {
  font-weight: 600;
  font-size: 13px;
  color: #343a40;
  display: flex;
  align-items: center;
  gap: 6px;
}

.table-preview-hint {
  font-size: 11px;
  color: #868e96;
  display: flex;
  align-items: center;
  gap: 4px;
}

.table-scroll-container {
  overflow-x: auto;
  overflow-y: auto;
  max-height: 450px;
  width: 100%;
}

/* ── Preview table ── */
.preview-table {
  border-collapse: collapse;
  white-space: nowrap;
  min-width: 100%;
  font-size: 13px;
}

.preview-table thead tr {
  position: sticky;
  top: 0;
  z-index: 2;
}

.preview-table th {
  padding: 10px 14px;
  background-color: #343a40;
  color: #ffffff;
  font-weight: 600;
  text-align: center;
  border: 1px solid #495057;
  white-space: nowrap;
}

.preview-table td {
  padding: 8px 14px;
  border: 1px solid #dee2e6;
  text-align: center;
  color: #212529;
  min-width: 120px;
}

.preview-table tbody tr:nth-child(even) {
  background-color: #f8f9fa;
}

.preview-table tbody tr:hover {
  background-color: #e8f4fd;
  transition: background-color 0.15s ease;
}

/* ── Toast line breaks ── */
:deep(.Vue-Toastification__toast-body) {
  white-space: pre-line;
}
</style>